import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public override componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // Log error to monitoring service if available
    if (import.meta.env.VITE_SENTRY_DSN) {
      // Log to Sentry or other error tracking service
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-paper-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-card border border-paper-200 sm:rounded-lg sm:px-10">
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold text-ink-900 mb-4">
                  Something went wrong
                </h2>
                <p className="text-ink-600 mb-6">
                  We encountered an unexpected error. Please try refreshing the page.
                </p>
                <div className="space-y-4">
                  <button
                    onClick={this.handleRetry}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full flex justify-center py-2 px-4 border border-paper-200 rounded-md shadow-sm text-sm font-medium text-ink-700 bg-white hover:bg-paper-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                  >
                    Refresh Page
                  </button>
                </div>
                {import.meta.env.DEV && this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-sm text-ink-400 hover:text-ink-700">
                      Error Details
                    </summary>
                    <pre className="mt-2 text-xs text-danger-500 overflow-auto">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary